import axios, { AxiosError, AxiosRequestConfig } from "axios";

const APP_BASENAME = "/app";

function getCsrfToken(): string | null {
  const metaTag = document.querySelector('meta[name="csrftoken"]');
  return metaTag ? metaTag.getAttribute("content") : null;
}

// Create axios instance with session-based auth and CSRF protection
export const AXIOS_INSTANCE = axios.create({
  baseURL: window.location.origin,
  withCredentials: true,
});

// Add CSRF token to all mutating requests
AXIOS_INSTANCE.interceptors.request.use((config) => {
  if (["post", "put", "patch", "delete"].includes(config.method || "")) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});

// Handle auth error responses
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      window.location.href = "/logout/";
    }
    if (error.response?.status === 403) {
      window.location.href = `${APP_BASENAME}/unauthorized`;
    }
    return Promise.reject(error);
  },
);

/**
 * Custom instance function for Orval.
 *
 * - On success: returns the response data directly
 * - On error: throws the error response data directly (not wrapped in AxiosError)
 *
 * This allows Orval's generated TError type (e.g., ApiErrorResponse) to work
 * correctly without needing to wrap with AxiosError in components.
 */
export function custom_instance<T>(config: AxiosRequestConfig): Promise<T> {
  return AXIOS_INSTANCE(config)
    .then(({ data }) => data)
    .catch((error: AxiosError) => {
      // Throw the error response data directly so TError types match
      if (error.response?.data) {
        throw error.response.data;
      }
      // Fallback for network errors or other issues without response data
      throw {
        message: error.message,
        details: {},
      };
    });
}

export default custom_instance;
